<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <h4 class="text-h4">{{$t("stock_movement")}}</h4>
                <p class="text-subtitle-1">{{$t("stock_movement_for_item")}}</p>
            </v-col>
        </v-row>
        <v-divider></v-divider>
        <v-form ref="form">
            <v-row class="my-2" align="center">
                <v-col>
                    <v-autocomplete
                        :label="$t('item')"
                        item-text="display_name"
                        item-value="id"
                        :items="items"
                        v-model="form.item_id"
                        outlined
                        :loading="itemsLoading"
                        @update:search-input="searchItems"
                        dense
                        hide-details
                        ></v-autocomplete>
                </v-col>
                <!-- <v-col>
                    <v-autocomplete
                        label="Warehouse"
                        item-text="display_name"
                        item-value="id"
                        :items="warehouses"
                        v-model="form.warehouse_id"
                        outlined
                        :loading="warehousesLoading"
                        @update:search-input="searchWarehouses"
                        dense
                        hide-details
                        ></v-autocomplete>
                </v-col> -->
                <v-col cols="2">
                    <DatePicker
                        :label="$t('date_from')"
                        v-model="form.from"
                        outlined
                        dense
                        hide-details/>
                </v-col>
                <v-col cols="2">
                    <DatePicker
                        :label="$t('date_to')"
                        v-model="form.to"
                        outlined
                        dense
                        hide-details/>
                </v-col>
                <v-col cols="2">
                    <v-btn block color="primary" @click="fetchAsync" :loading="loading">
                        <v-icon left>mdi-magnify</v-icon>
                        {{$t("search")}}
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
        <v-row>
            <v-col>
                <v-progress-linear indeterminate v-if="loading"/>
                <v-simple-table dense>
                    <thead>
                        <tr>
                            <th>{{$t("date")}}</th>
                            <th>{{$t("reference")}}</th>
                            <th>{{$t("description")}}</th>
                            <th>{{$t("item_average_cost")}}</th>
                            <th>{{$t("quantity_in")}}</th>
                            <th>{{$t("quantity_out")}}</th>
                            <th>{{$t("remaining_quantity")}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="data && data.opening" class="secondary">
                            <td colspan="6" class="font-weight-bold">{{$t("opening_on_hand")}} - {{formatDate(data.period.from)}}</td>
                            <td class="font-weight-bold">{{formatNumber(data.opening.on_hand, 2)}}</td>
                        </tr>
                        <tr v-for="(item, index) in data.running" :key="index">
                            <td>{{item.date}}</td>
                            <td>{{item.reference_code}}</td>
                            <td>{{item.remark}}</td>
                            <td class="grey--text">{{formatNumber(item.value / item.quantity, 2)}}</td>
                            <td class="success--text">{{renderQuantityIn(item.quantity)}}</td>
                            <td class="error--text">{{renderQuantityOut(item.quantity)}}</td>
                            <td class="font-weight-bold">
                                {{formatNumber(item.on_hand, 2)}}
                            </td>                
                        </tr>
                        <tr v-if="data && data.running && data.running.length == 0">
                            <td colspan="7" class="text-center">{{$t("no_movement")}}</td>
                        </tr>
                        <tr v-if="data && data.opening" class="secondary font-weight-bold">
                            <td colspan="4">{{$t("closing_on_hand")}} - {{formatDate(data.period.to)}}</td>
                            <td class="success--text">{{ formatNumber(totalQuantityIn(data.running), 2) }}</td>
                            <td class="error--text">{{ formatNumber(totalQuantityOut(data.running), 2) }}</td>
                            <td>{{formatNumber(data.closing.on_hand, 2)}}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { formatNumber } from 'accounting';
import DatePicker from './../../components/common/DatePicker.vue';
import moment from 'moment';
import itemService from './../../services/stock/item';
import warehouseService from './../../services/warehouse/warehouse';
import stockService from './../../services/stock/reports';
import { isRequired } from './../../utils/validations';
import { getTotal } from '../../utils/accounting';

export default {
    name: "StockMovementReportView",
    components: {
        DatePicker
    },
    data() {
        return {
            items: [],
            warehouses: [],
            data: [],
            form: {
                warehouse_id: null,
                item_id: null,
                from: moment().format("YYYY-MM-DD"),
                to: moment().format("YYYY-MM-DD")
            },
            loading: false, 
            itemsLoading: false,
            warehousesLoading: true
        }
    },
    methods: {
        isRequired,
        formatNumber,
        formatDate(date) {
            return moment(date).format("YYYY-MM-DD");
        },
        renderQuantityIn(quantity) {
            quantity = Number(quantity);
            return quantity > 0 ? formatNumber(quantity, 2) : "";
        },
        renderQuantityOut(quantity) {
            quantity = Number(quantity);
            return quantity < 0 ? formatNumber(Math.abs(quantity), 2) : "";
        },
        searchItems(query) {
            this.itemsLoading = true;
            itemService.search(query).then(response => {
                this.items = Array.from(new Set([...this.items, ...response.data]));
            }).finally(() => {
                this.itemsLoading = false;
            })
        },
        searchWarehouses(query) {
            this.warehousesLoading = true;
            warehouseService.search(query).then(response => {
                this.warehouses = Array.from(new Set([...this.warehouses, ...response.data]));
            }).finally(() => {
                this.warehousesLoading = false;
            })
        },
        fetchAsync() {
            this.data = [];
            this.loading = true;
            stockService.getStockMovementReport(this.form).then(response => {
                this.data = response.data;
            }).finally(() => {
                this.loading = false;
            })
        },
        /**
         * 
         * @param {Array<{quantity}>} running 
         */
        totalQuantityIn(running) {
            return Math.abs(getTotal(running.filter(each => each.quantity > 0), "quantity")) 
        },
        /**
         * 
         * @param {Array<{quantity}>} running 
         */
        totalQuantityOut(running) {
            return Math.abs(getTotal(running.filter(each => each.quantity < 0), "quantity")) 
        }
    },
    created() {
       this.form.warehouse_id = this.$route.params.warehouseId
    }
}
</script>

<style>

</style>